import React, { useState } from 'react'
import { Button, Icon, Label, List, Popup, Table } from 'semantic-ui-react'
import DropdownInsideScrollableParent from '../../../../../components/Dropdown/DropdownInsideScrollableParent'
import { useWindowSize } from '../../../../../hooks/useWindowSize'
import { abbreviateString } from '../../../../../utils/abbreviateString'
import { currencyFormat } from '../../../../../utils/currencyFormat'
import { IPassThroughCost } from '../../../types/directAndPassTroughCosts'
import { IProject, IProjectLabel, IProjectPhase } from '../../../types/project'
import '../BudgetTableComponent.css'


interface StateProps {
  costs: IPassThroughCost[]
  calcTotalCost: number
  calcTotalThereofPaid: number
  project: IProject
  name: string
}

interface DispatchProps {
  updateCost: (cost: IPassThroughCost) => void
}

type Props = StateProps & DispatchProps

const ServiceProviderPassTroughBudgetTable = (props: Props): JSX.Element => {
  const { costs, calcTotalCost, calcTotalThereofPaid, updateCost, project, name } = props
  const [collapseBudget, setCollapseBudget] = useState(true)
  const [costState, setCostState] = useState<IPassThroughCost | undefined>()

  const size = useWindowSize().width

  const renderCollapseRows = () => {
    return (
      <Table.Row style={{ boxShadow: 'inset 0rem 0rem 10rem #F4F7F9', borderRadius: 0, height: '50px' }}>
        <Table.Cell colSpan={5} style={{ borderTop: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', paddingLeft: '40px' }}>
            <Icon
              name={collapseBudget ? 'chevron circle down' : 'chevron circle right'}
              style={{ margin: 0, color: '#172533', width: '14px', height: '14px' }}
              onClick={() => setCollapseBudget(!collapseBudget)}
            />
            <div style={{ fontSize: '1.28rem', color: '#172533', marginLeft: '8px', letterSpacing: '.02rem', fontWeight: 'bold' }}>{name}</div>
          </div>
        </Table.Cell>
        <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }}>
          {calcTotalCost !== 0 ?
            <div style={{ display: 'flex', float: 'right', color: '#232C58' }}>
              <p style={{ margin: 0 }}>{currencyFormat(calcTotalCost)[0]} {currencyFormat(calcTotalCost).split(',')[0].replace(currencyFormat(calcTotalCost)[0], '')}</p>
              <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcTotalCost).split(',')[1]}</p>
            </div>
            : <p style={{ color: '#232C58', textAlign: 'right' }}>- €</p>}</Table.Cell>
        <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }}>
          {calcTotalThereofPaid !== 0 ?
            <div style={{ display: 'flex', float: 'right', color: '#232C58', paddingRight: size > 1800 ? '40px' : '20px' }}>
              <p style={{ margin: 0 }}>{currencyFormat(calcTotalThereofPaid)[0]} {currencyFormat(calcTotalThereofPaid).split(',')[0].replace(currencyFormat(calcTotalThereofPaid)[0], '')}</p>
              <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcTotalThereofPaid).split(',')[1]}</p>
            </div>
            : <p style={{ color: '#232C58', textAlign: 'right', paddingRight: size > 1800 ? '40px' : '20px' }}>- €</p>}</Table.Cell>
        <Table.Cell style={{ borderTop: 'none' }} />
      </Table.Row>
    )
  }

  const renderPhasePopup = (phases: IProjectPhase[], all: boolean) => {
    const _phases = all ? phases : phases.slice(1)
    return (
      <List>
        {_phases.map((p, i) => {
          return (
            <List.Item key={i}>
              <Label
                content={abbreviateString(p.name, 15, true, 17)}
                style={{
                  background: p.color + '33',
                  color: p.color,
                  borderRadius: '8px',
                  height: '26px'
                 }}
              />
            </List.Item>
          )
        })}
      </List>
    )
  }

  const renderPhaseLabel = (phases: IProjectPhase[]) => {
    const counter = phases.length - 1
    let _phases = undefined
    if (phases.length !== project.phases.length) {
      _phases = phases.map((phase, idx) => {
        if (idx < 1) {
          return (
            <Label
              key={idx}
              content={abbreviateString(phase.name, 15, true, 17)}
              style={{
                background: phase.color + '33',
                color: phase.color,
                borderRadius: '8px',
                height: '26px'
              }}
            />
          )
        }
      })
      if (phases.length > 1) {
        const label =
          <Popup
            key={phases.length}
            content={renderPhasePopup(phases, false)}
            trigger={
              <Label
                content={`+${counter}`}
                style={{
                  background: '#E0E0E0',
                  color: '#696C72',
                  borderRadius: '8px',
                  height: '26px',
                  cursor: 'pointer'
                }}
              />
            }
          />
        _phases = [..._phases, label]
      }
    } else {
      const label =
        <Popup
          key={phases.length}
          content={renderPhasePopup(phases, true)}
          trigger={
            <Label
              content={'All Phases'}
              style={{
                background: '#E0E0E0',
                color: '#696C72',
                borderRadius: '8px',
                height: '26px'
              }}
            />
          }
        />
      _phases = label
    }
    return <div style={{ display: 'flex' }}>{_phases}</div>
  }

  const renderLabels = (labels: IProjectLabel[]) => {
    const counter = labels.length - 1
    let _labels = undefined
    if (labels.length !== project.labels.length) {
      _labels = labels.map((label, idx) => {
        if (idx < 1) {
          return (
            <Label
              key={label.id}
              content={abbreviateString(label.name, 15, true, 17)}
              style={{
                background: label.color,
                borderRadius: '8px',
                height: '26px'
              }}
            />
          )
        }
      })
      if (labels.length > 1) {
        const label =
          <Label
            key={labels.length}
            content={`+${counter}`}
            style={{
              background: '#E0E0E0',
              color: '#696C72',
              borderRadius: '8px',
              height: '26px'
            }}
          />
        _labels = [..._labels, label]
      }
    } else {
      const label =
        <Label
          key={labels.length}
          content={'All Labels'}
          style={{
            background: '#E0E0E0',
            color: '#696C72',
            borderRadius: '8px',
            height: '26px'
          }}
        />
      _labels = label
    }
    return <div style={{ display: 'flex' }}>{_labels}</div>
  }

  const onChangeLabels = (e: React.SyntheticEvent<HTMLElement, Event>, label: IProjectLabel, costId: string | number, checked: boolean) => {
    if (!checked) {
      costs.forEach(cost => {
        if (cost.id === costId) {
          cost.labels = [...cost.labels, ...project.labels.filter(l => l.name === label.name)]
          updateCost(cost as IPassThroughCost)
        }
      })
    } else {
      costs.forEach(cost => {
        if (cost.id === costId) {
          cost.labels = cost.labels.filter(l => l.name !== label.name)
          updateCost(cost as IPassThroughCost)
        }
      })
    }
  }

  const renderCheckbox = (checked: boolean) => {
    if (checked) {
      return (
        <Button
          style={{ height: '1.25rem', width: '1.25rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          primary={true}
          icon='check'
        />
      )
    }
    return <div style={{ cursor: 'pointer', marginRight: '0.3rem', background: '#E0E1E2', height: '1.55rem', width: '1.55rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }} />
  }

  const renderLabelsDropdown = (costId: string | number, labels: IProjectLabel[]) => {
    const _cost = costs.filter(c => c.id === costId)[0]
    const _options = project.labels.map(label => {
      return {
        key: label.id,
        value: label.id,
        text: (
          <div style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => onChangeLabels(e, label, costId, labels.map(l => l.id).indexOf(label.id) > -1)}>
            {renderCheckbox(labels.map(l => l.id).indexOf(label.id) > -1)}
            <Label
              content={label.name.length > 14 ? `${label.name.slice(0, 12)}...` : label.name}
              style={{ maxWidth: '7.5rem', background: label.color, borderRadius: '8px', height: '1.5rem', paddingTop: '.3rem', minWidth: '4rem', textAlign: 'center' }}
            />
          </div>
        ),
        selected: false
      }
    })
    return (
      <DropdownInsideScrollableParent
        iconFontSize='1.2rem'
        iconName='caret down'
        iconColor='#08D6A1'
        options={_options}
        content={_cost && _cost.labels.filter(l => l.name !== '').length !== 0 ? renderLabels(_cost.labels) : ''}
        placeholder='Allocate Label'
        width={'11rem'}
        buttonWidth='11rem'
      />
    )
  }

  const handleMouseOver = (cost: IPassThroughCost) => {
    setCostState(cost)
  }

  const renderRows = () => {
    return costs.map((a, idx) => {
      const total = +a.unit_price * +a.quantity
      return (
        <Table.Row key={idx} className={collapseBudget ? 'show-row' : 'collapse-row'} style={{ background: idx % 2 ? '#F9FBFC' : '' }}>
          <Table.Cell className={idx % 2 ? 'custom-left-border-cell' : ''} id='disableCloseModal' style={{ border: 'none' }} >
            <strong id='disableCloseModal' style={{ color: '#232C58', textAlign: 'left', paddingLeft: '40px' }}>
              {a.offer_position}
            </strong>
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none' }} textAlign='left'>
            {a.project_phases ? renderPhaseLabel(a.project_phases) : null}
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none' }}>
            <p style={{ color: '#232C58', textAlign: 'left' }}>
              {a.supplier}
            </p>
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none' }}>
            <p style={{ color: '#232C58', textAlign: 'center', fontWeight: 'bold' }}>{a.quantity}</p>
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }}>
            {a.unit_price !== 0 ?
              <div style={{ display: 'flex', float: 'right', color: '#232C58' }}>
                <p style={{ margin: 0 }}>{currencyFormat(+a.unit_price)[0]} {currencyFormat(+a.unit_price).split(',')[0].replace(currencyFormat(+a.unit_price)[0], '')}</p>
                <p style={{ color: '#BEC8C3' }}>,{currencyFormat(+a.unit_price).split(',')[1]}</p>
              </div>
              : <p style={{ color: '#232C58', textAlign: 'right' }}>- €</p>}
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }} textAlign='right'>
            {total !== 0 ?
              <div style={{ display: 'flex', float: 'right', color: '#232C58' }}>
                <p style={{ margin: 0 }}>{currencyFormat(total)[0]} {currencyFormat(total).split(',')[0].replace(currencyFormat(total)[0], '')}</p>
                <p style={{ color: '#BEC8C3' }}>,{currencyFormat(total).split(',')[1]}</p>
              </div>
              : <p style={{ color: '#232C58', textAlign: 'right' }}>- €</p>}
          </Table.Cell>

          <Table.Cell style={{ borderTop: 'none', fontWeight: 500 }} textAlign='right'>
            {a.thereof_paid !== 0 ?
              <div style={{ display: 'flex', float: 'right', color: '#232C58', paddingRight: size > 1800 ? '40px' : '20px' }}>
                <p style={{ margin: 0 }}>{currencyFormat(+a.thereof_paid)[0]} {currencyFormat(+a.thereof_paid).split(',')[0].replace(currencyFormat(+a.thereof_paid)[0], '')}</p>
                <p style={{ color: '#BEC8C3' }}>,{currencyFormat(+a.thereof_paid).split(',')[1]}</p>
              </div>
              : <p style={{ color: '#232C58', textAlign: 'right', paddingRight: size > 1800 ? '40px' : '20px' }}>- €</p>}
          </Table.Cell>

          <Table.Cell className={idx % 2 ? 'custom-right-border-cell cell-no-padding' : 'cell-no-padding'} onMouseOver={() => handleMouseOver(a)} style={{ borderTop: 'none', width: '12rem' }} textAlign='left'>
            {(costState && costState.id === a.id) ? renderLabelsDropdown(a.id, a.labels) : a.labels.length !== 0 ? renderLabels(a.labels) : <p>Allocate Label</p>}
          </Table.Cell>
        </Table.Row>
      )
    })
  }

  return (
    <React.Fragment>
      {renderCollapseRows()}
      {renderRows()}
    </React.Fragment>
  )
}

export default ServiceProviderPassTroughBudgetTable
